import { useEffect, useRef, useState } from "react";
import { FaSignOutAlt, FaUser, FaHome } from "react-icons/fa";
import { GrFavorite } from "react-icons/gr";
import { IoCartOutline } from "react-icons/io5";
import { CgProfile } from "react-icons/cg";
import Logo from "./Logo";

import useClickAway from "../../../hooks/ui/useClickAway";
import CartDropdown from "../cart/CartDropdown";
import WishlistDropdown from "../wishlist/WishlistDropdown";

import {
  DASHBOARD_ROUTE,
  LOGIN_ROUTE,
  USER_DASHBOARD_ROUTE,
  HOME_ROUTE,
} from "../../../routes/web/rootRoutes";

import { Link, useNavigate } from "react-router-dom";
import authManager from "../../../utils/authManager";
import { useDispatch, useSelector } from "react-redux";
import { getHomeData } from "../../../store/slice/userActions";
import apiRequest from "../../../utils/apiRequest";
import {
  resetUserProfile,
  setUserProfile,
} from "../../../store/slice/globalStore";
import { resetUserData } from "../../../store/slice/userSlice";

const Header = () => {
  const isLoggedIn = authManager.hasAccessToken();
  const role = authManager.getRole();
  const userProfile = useSelector((state) => state.globalState.userProfile);

  const cartRef = useRef(null);
  const wishlistRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const dropdownRef = useRef(null);

  useClickAway(cartRef, () => setIsCartOpen(false));
  useClickAway(wishlistRef, () => setIsWishlistOpen(false));
  useClickAway(dropdownRef, () => setProfileOpen(false));

  useEffect(() => {
    apiRequest.getProfile().then((res) => {
      dispatch(setUserProfile(res?.data?.data));
    });
  }, []);

  const wishlistItems = useSelector((state) => state.user.wishlist);
  const cartItems = useSelector((state) => state.user.cart);
  const loading = useSelector((state) => state.user.homeLoading);

  const wishlistCount = wishlistItems?.products?.length || 0;
  const cartCount = cartItems?.products?.length || 0;

  useEffect(() => {
    dispatch(getHomeData());
  }, []);

  const handleLogout = () => {
    authManager.deleteAccessToken();
    dispatch(resetUserProfile());
    dispatch(resetUserData());
    navigate(LOGIN_ROUTE);
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            
            <Link to={HOME_ROUTE} className="flex items-center gap-2">
              <Logo />
              <h1 className="hidden sm:block text-lg font-bold bg-gradient-to-r from-orange-500 to-orange-600 text-transparent bg-clip-text">
                MOBO EAT
              </h1>
            </Link>

            <div className="flex items-center gap-4">
              
              <div ref={wishlistRef} className="relative  block">
                <button
                  className="relative p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setIsWishlistOpen(!isWishlistOpen)}
                >
                  <GrFavorite className="text-lg text-gray-700" />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-medium">
                      {wishlistCount}
                    </span>
                  )}
                </button>

                {isWishlistOpen && (
                  <WishlistDropdown onClose={() => setIsWishlistOpen(false)} loading={loading} products={wishlistItems} />
                )}
              </div>

              <div ref={cartRef} className="relative block">
                <button
                  className="relative p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setIsCartOpen(!isCartOpen)}
                >
                  <IoCartOutline className="text-xl text-gray-700" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-medium">
                      {cartCount}
                    </span>
                  )}
                </button>

                {isCartOpen && (
                  <CartDropdown onClose={() => setIsCartOpen(false)} loading={loading} products={cartItems} />
                )}
              </div>

              <div ref={dropdownRef} className="relative">
                {isLoggedIn ? (
                  <>
                    <button
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setProfileOpen(!profileOpen)}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center">
                        <FaUser className="text-white text-xs" />
                      </div>
                      <span className="hidden lg:block text-sm font-medium text-gray-700">
                        {userProfile?.name?.split(" ")[0] || "User"}
                      </span>
                    </button>

                    {profileOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200">
                        <div className="p-3 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">{userProfile?.name}</p>
                          <p className="text-xs text-gray-500">{userProfile?.email}</p>
                        </div>
                        
                        <Link
                          to={role === "User" ? USER_DASHBOARD_ROUTE : DASHBOARD_ROUTE}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-gray-700 text-sm"
                        >
                          <CgProfile className="text-base" />
                          My Account
                        </Link>

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-red-600 text-sm border-t border-gray-100"
                        >
                          <FaSignOutAlt className="text-base" />
                          Sign Out
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={LOGIN_ROUTE}
                    className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all text-sm font-medium"
                  >
                    Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <nav className="fixed bottom-0 left-0 right-0 z-[10] bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.08)] md:hidden border-t border-gray-100">
        <div className="flex justify-around items-center py-2">
          
          <button 
            onClick={() => navigate(HOME_ROUTE)} 
            className="flex flex-col items-center gap-1 py-1 px-3"
          >
            <FaHome className="text-lg text-gray-600" />
            <span className="text-[10px] text-gray-600">Home</span>
          </button>

          <button 
            onClick={() => setIsWishlistOpen(!isWishlistOpen)}
            className="flex flex-col items-center gap-1 py-1 px-3 relative"
          >
            <GrFavorite className="text-lg text-gray-600" />
            {wishlistCount > 0 && (
              <span className="absolute top-0 right-1 bg-pink-500 text-white rounded-full text-[9px] min-w-[16px] h-[16px] flex items-center justify-center font-medium">
                {wishlistCount} 
              </span>
            )}
            <span className="text-[10px] text-gray-600">Wishlist</span>
          </button>

          <button 
            onClick={() => setIsCartOpen(!isCartOpen)} 
            className="flex flex-col items-center gap-1 py-1 px-3 relative"
          >
            <IoCartOutline className="text-xl text-gray-600" />
            {cartCount > 0 && (
              <span className="absolute top-0 right-1 bg-orange-500 text-white rounded-full text-[9px] min-w-[16px] h-[16px] flex items-center justify-center font-medium">
                {cartCount}
              </span>
            )}
            <span className="text-[10px] text-gray-600">Cart</span>
          </button>


          <button
            onClick={() =>
              isLoggedIn ? navigate(USER_DASHBOARD_ROUTE) : navigate(LOGIN_ROUTE)
            }
            className="flex flex-col items-center gap-1 py-1 px-3"
          >
            <CgProfile className="text-lg text-gray-600" />
            <span className="text-[10px] text-gray-600">Profile</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default Header;